import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Row, Col, Card, Badge, Button, Modal, Nav, Tab, Spinner, Form } from 'react-bootstrap';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
    getAllObservations, 
    getAllHiveActivityLogs, 
    getAllApiariesService 
} from '../../../../services/apiaryService';
import { readCookie } from '../../cookie';

// Styled Components
const MainWrapper = styled.div`
    padding: ${props => props.isEmbedded ? '20px 0' : '24px'};
    margin-left: ${props => props.isEmbedded ? '0' : '15%'};
    width: ${props => props.isEmbedded ? '100%' : '85%'};
    background: #f8fafc;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-sizing: border-box;

    @media (max-width: 992px) {
        margin-left: 0;
        width: 100%;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 24px;
`;

const DateControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    padding: 6px 12px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const WhatsAppCard = styled.div`
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid #dcf8c6;
    box-shadow: 0 4px 20px rgba(18, 140, 126, 0.08);
    overflow: hidden;
    margin-bottom: 24px;
`;

const WhatsAppHeader = styled.div`
    background: linear-gradient(135deg, #075e54, #128c7e);
    color: #ffffff;
    padding: 14px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const WhatsAppBody = styled.div`
    padding: 20px;
    background: #f0fdf4;
`;

const MessageTextarea = styled.pre`
    background: #ffffff;
    color: #1f2937;
    font-family: 'Consolas', 'Menlo', 'Courier New', monospace;
    font-size: 13.5px;
    line-height: 1.6;
    padding: 16px 20px;
    border-radius: 12px;
    border: 1px solid #bbf7d0;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
    max-height: 280px;
    overflow-y: auto;
    box-shadow: inset 0 1px 3px rgba(0,0,0,0.03);
`;

const StatCard = styled(Card)`
    border: none;
    border-radius: 14px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 100%;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0,0,0,0.08);
    }
`;

const StatIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    background: ${props => props.bg || '#eff6ff'};
    color: ${props => props.color || '#2563eb'};
`;

const CustomTable = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e2e8f0;

    th {
        background: #f8fafc;
        color: #475569;
        font-weight: 700;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 14px 16px;
        border-bottom: 2px solid #e2e8f0;
    }

    td {
        padding: 14px 16px;
        font-size: 13px;
        color: #334155;
        border-bottom: 1px solid #f1f5f9;
        vertical-align: middle;
    }

    tr:last-child td {
        border-bottom: none;
    }

    tr:hover td {
        background: #f8fafc;
    }
`;

export default function DailyApiarySummary({ isEmbedded = false }) {
    // Current date state (default: today)
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [groupTitle, setGroupTitle] = useState('Golden Rev. Group');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    
    // Data states
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [observations, setObservations] = useState([]);
    const [hiveActivityLogs, setHiveActivityLogs] = useState([]);
    const [apiariesList, setApiariesList] = useState([]);
    const [allUsersList, setAllUsersList] = useState([]);
    
    // UI states
    const [activeTab, setActiveTab] = useState('executives');
    const [copied, setCopied] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedObservation, setSelectedObservation] = useState(null);

    // Fetch all foundational data
    const fetchAllData = useCallback(async () => {
        try {
            setRefreshing(true);
            const currentToken = JSON.parse(readCookie('access_token') || 'null') || readCookie('access_token');
            const HUMBLEBEE_API_URL = process.env.REACT_APP_HUMBLEBEE_API_URL || 'https://devapi.thehumblebee.co/api/v1/humblebee';

            const [obsRes, logsRes, apiariesRes, usersRes] = await Promise.allSettled([
                getAllObservations(),
                getAllHiveActivityLogs(),
                getAllApiariesService(),
                axios.get(`${HUMBLEBEE_API_URL}/users?limit=10000&role=all`, {
                    headers: {
                        source: 'humblebee_admin',
                        Authorization: 'Bearer ' + currentToken
                    }
                })
            ]);

            if (obsRes.status === 'fulfilled' && Array.isArray(obsRes.value)) {
                setObservations(obsRes.value);
            }
            if (logsRes.status === 'fulfilled' && Array.isArray(logsRes.value)) {
                setHiveActivityLogs(logsRes.value);
            }
            if (apiariesRes.status === 'fulfilled') {
                const list = apiariesRes.value?.data || apiariesRes.value;
                setApiariesList(Array.isArray(list) ? list : []);
            }
            if (usersRes.status === 'fulfilled' && usersRes.value?.data) {
                const users = usersRes.value.data.userInfo || usersRes.value.data.users || usersRes.value.data.data || [];
                setAllUsersList(Array.isArray(users) ? users : []);
            }
        } catch (error) {
            console.error("Error loading daily apiary summary data:", error);
            toast.error("Failed to load some daily summary data");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Map apiaries by id for fast name lookup
    const apiaryNameMap = useMemo(() => {
        const map = {};
        apiariesList.forEach(apiary => {
            const id1 = apiary._id ? apiary._id.toString() : null;
            const id2 = apiary.apiary_id ? apiary.apiary_id.toString() : null;
            const name = apiary.apiary_name || apiary.name || 'Unnamed Apiary';
            if (id1) map[id1] = name;
            if (id2) map[id2] = name;
        });
        return map;
    }, [apiariesList]);

    // Map users by id and phone for fast identity resolution
    const usersMap = useMemo(() => {
        const idMap = {};
        const phoneMap = {};
        allUsersList.forEach(user => {
            const uInfo = user.userInfo || user;
            const uid = uInfo._id ? uInfo._id.toString() : (user._id ? user._id.toString() : null);
            const mobile = (uInfo.mobileNumber || uInfo.mobile_number || '').toString().replace(/\D/g, '').slice(-10);
            const name = uInfo.name || uInfo.full_name || 'Team Member';
            const roles = uInfo.userRoles || (uInfo.userRole ? [uInfo.userRole] : []);
            
            const userObj = {
                id: uid,
                name,
                mobile,
                roles,
                raw: user
            };

            if (uid) idMap[uid] = userObj;
            if (mobile && mobile !== '0000000000') phoneMap[mobile] = userObj;
        });
        return { idMap, phoneMap };
    }, [allUsersList]);

    // Filtered Community Observations for Selected Date
    const dayObservations = useMemo(() => {
        const targetDate = moment(selectedDate, 'YYYY-MM-DD');
        return observations.filter(obs => {
            const dateVal = obs.observation_date || obs.created_at;
            if (!dateVal) return false;
            return moment(dateVal).isSame(targetDate, 'day');
        });
    }, [observations, selectedDate]);

    // Filtered Household Hive Activity Logs for Selected Date
    const dayHouseholdLogs = useMemo(() => {
        const targetDate = moment(selectedDate, 'YYYY-MM-DD');
        return hiveActivityLogs.filter(log => {
            if (log.isDeleted) return false;
            const dateVal = log.created_at || log.date || log.updated_at;
            if (!dateVal) return false;
            return moment(dateVal).isSame(targetDate, 'day');
        });
    }, [hiveActivityLogs, selectedDate]);

    // Aggregate Executive Activities for Selected Date
    const { activeExecutives, inactiveExecutives, allExecutivesCount } = useMemo(() => {
        const execMap = {};

        // Helper to get or create an executive record
        const getOrCreateExec = (id, fallbackName, mobile, roleHint) => {
            let userObj = id ? usersMap.idMap[id.toString()] : null;
            if (!userObj && mobile) {
                const cleanMob = mobile.toString().replace(/\D/g, '').slice(-10);
                userObj = usersMap.phoneMap[cleanMob];
            }

            const execId = userObj?.id || id?.toString() || fallbackName || 'unknown';
            const execName = userObj?.name || fallbackName || 'Unknown Executive';
            const execMobile = userObj?.mobile || mobile || '-';
            const roles = userObj?.roles || (roleHint ? [roleHint] : ['Bee Mitra']);

            if (!execMap[execId]) {
                execMap[execId] = {
                    id: userObj?.id || id || null,
                    name: execName,
                    mobile: execMobile,
                    roles,
                    householdVisits: [],
                    communityObservations: []
                };
            }
            return execMap[execId];
        };

        // 1. Process Household Visits
        dayHouseholdLogs.forEach(log => {
            const visitorId = log.visited_by_id?.$oid || log.visited_by_id || log.visited_by?._id || log.createdBy?._id || log.created_by?.$oid || log.created_by || log.user_id;
            const visitorName = log.visited_by_name || log.visited_by?.name || log.userName?.name || log.logged_by || log.createdBy?.name || 'Visitor';
            const mobile = log.mobile_number || log.beekeeper_mobile || log.phone;
            const role = log.added_by_user_type || (log.visited_by_name ? 'bee_mitra' : 'beekeeper');

            const exec = getOrCreateExec(visitorId, visitorName, mobile, role);
            exec.householdVisits.push(log);
        });

        // 2. Process Community Observations (from humblebee_observations)
        dayObservations.forEach(obs => {
            const observerId = obs.observed_by?.$oid || obs.observed_by?._id || obs.observed_by;
            const observerName = obs.observed_by_name || obs.observer_name || obs.userName?.name || 'Observer';

            const exec = getOrCreateExec(observerId, observerName, null, 'Field Executive');
            exec.communityObservations.push(obs);
        });

        // Filter and sort active executives
        const active = Object.values(execMap).filter(e => 
            e.householdVisits.length > 0 || e.communityObservations.length > 0
        ).sort((a, b) => {
            const totalA = a.householdVisits.length + a.communityObservations.length;
            const totalB = b.householdVisits.length + b.communityObservations.length;
            return totalB - totalA;
        });

        // 3. Identify Inactive Team Members (Registered BeeMitras / Field Staff with 0 visits today)
        const activeIds = new Set(active.map(a => a.id ? a.id.toString() : a.name.toLowerCase()));
        const inactive = [];

        allUsersList.forEach(u => {
            const uInfo = u.userInfo || u;
            const uid = uInfo._id ? uInfo._id.toString() : (u._id ? u._id.toString() : null);
            const name = uInfo.name || uInfo.full_name;
            if (!name) return;

            const roles = uInfo.userRoles || (uInfo.userRole ? [uInfo.userRole] : []);
            const isFieldStaff = roles.some(r => 
                ['bee_mitra', 'staff', 'field_view_app_admin', 'field_executive', 'cluster_manager', 'mentor'].includes(r)
            );

            // If user is a field staff member and did not perform any visit today
            if (isFieldStaff && uid && !activeIds.has(uid) && !activeIds.has(name.toLowerCase())) {
                inactive.push({
                    id: uid,
                    name,
                    mobile: uInfo.mobileNumber || uInfo.mobile_number || '-',
                    roles
                });
            }
        });

        inactive.sort((a, b) => a.name.localeCompare(b.name));

        return {
            activeExecutives: active,
            inactiveExecutives: inactive,
            allExecutivesCount: active.length + inactive.length
        };
    }, [dayHouseholdLogs, dayObservations, usersMap, allUsersList]);

    // KPI Metrics
    const totalHouseholdVisits = dayHouseholdLogs.length;
    const totalCommunityObservations = dayObservations.length;
    const totalVisitsCount = totalHouseholdVisits + totalCommunityObservations;

    // Generated WhatsApp Text (Matches Notebook in Image 4 precisely)
    const whatsappMessage = useMemo(() => {
        const formattedDate = moment(selectedDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
        let text = `${groupTitle}\nWHATSAPP DAILY 9PM DIGEST\n${formattedDate} - Apiary visit & Hive observation report\n\n`;

        if (activeExecutives.length === 0) {
            text += `No apiary visits or observations recorded on this date.\n\n`;
        } else {
            activeExecutives.forEach((exec, idx) => {
                const hCount = exec.householdVisits.length;
                const cCount = exec.communityObservations.length;
                const lines = [];

                if (hCount > 0) {
                    lines.push(`${hCount} household ${hCount === 1 ? 'apiary' : 'apiaries'} visited`);
                }
                if (cCount > 0) {
                    lines.push(`${cCount} community ${cCount === 1 ? 'apiary' : 'apiaries'}`);
                }

                if (lines.length === 1) {
                    text += `${idx + 1}) ${exec.name} - ${lines[0]}\n`;
                } else if (lines.length > 1) {
                    text += `${idx + 1}) ${exec.name} - ${lines[0]}\n                - ${lines[1]}\n`;
                }
            });
            text += '\n';
        }

        if (inactiveExecutives.length > 0) {
            const inactiveNames = inactiveExecutives.map(e => e.name).join(', ');
            text += `No apiary visit :- ${inactiveNames}\n`;
        }

        return text.trim();
    }, [groupTitle, selectedDate, activeExecutives, inactiveExecutives]);

    // Copy to clipboard handler
    const handleCopy = () => {
        navigator.clipboard.writeText(whatsappMessage);
        setCopied(true);
        toast.success("WhatsApp digest copied to clipboard!");
        setTimeout(() => setCopied(false), 2500);
    };

    // Share directly via WhatsApp Web/App
    const handleShareWhatsApp = () => {
        const encoded = encodeURIComponent(whatsappMessage);
        window.open(`https://api.whatsapp.com/send?text=${encoded}`, '_blank');
    };

    // Date step navigators
    const handlePrevDay = () => {
        setSelectedDate(prev => moment(prev, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD'));
    };

    const handleNextDay = () => {
        setSelectedDate(prev => moment(prev, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD'));
    };

    const handleSetToday = () => {
        setSelectedDate(moment().format('YYYY-MM-DD'));
    };

    // Filtered executive list for search
    const filteredActiveExecutives = useMemo(() => {
        if (!searchQuery) return activeExecutives;
        const q = searchQuery.toLowerCase();
        return activeExecutives.filter(e => 
            e.name.toLowerCase().includes(q) || e.mobile.includes(q)
        );
    }, [activeExecutives, searchQuery]);

    if (loading) {
        return (
            <MainWrapper isEmbedded={isEmbedded}>
                <div className="text-center p-5 mt-5">
                    <Spinner animation="border" variant="primary" size="lg" />
                    <p className="mt-3 text-muted" style={{ fontSize: '15px', fontWeight: 600 }}>
                        Loading Daily Apiary Visit & Observation Digest...
                    </p>
                </div>
            </MainWrapper>
        );
    }

    return (
        <MainWrapper isEmbedded={isEmbedded}>
            {/* Top Navigation & Controls */}
            <HeaderContainer>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 800, color: '#1e293b', fontSize: '24px' }}>
                        📊 Daily Apiary Visit & Observation Digest
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13.5px' }}>
                        Executive-wise daily summary of household visits & community apiary observations
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <DateControls>
                        <Button 
                            variant="light" 
                            size="sm" 
                            onClick={handlePrevDay} 
                            style={{ fontWeight: 700, padding: '4px 10px', borderRadius: '8px' }}
                            title="Previous Day"
                        >
                            ◀
                        </Button>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                border: 'none',
                                fontWeight: 700,
                                color: '#1e293b',
                                outline: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                background: 'transparent'
                            }}
                        />
                        <Button 
                            variant="light" 
                            size="sm" 
                            onClick={handleNextDay} 
                            style={{ fontWeight: 700, padding: '4px 10px', borderRadius: '8px' }}
                            title="Next Day"
                        >
                            ▶
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={handleSetToday} 
                            style={{ fontWeight: 700, borderRadius: '8px', padding: '4px 10px', fontSize: '12px' }}
                        >
                            Today
                        </Button>
                    </DateControls>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={fetchAllData}
                        disabled={refreshing}
                        style={{ fontWeight: 700, borderRadius: '10px', padding: '8px 14px' }}
                    >
                        <i className={`fa fa-refresh mr-2 ${refreshing ? 'fa-spin' : ''}`}></i>
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </div>
            </HeaderContainer>

            {/* KPI Summary Cards */}
            <Row className="mb-4">
                <Col md={3} sm={6} className="mb-3">
                    <StatCard>
                        <Card.Body style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px' }}>
                            <StatIcon bg="#eff6ff" color="#2563eb">
                                <i className="fa fa-tachometer"></i>
                            </StatIcon>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                    Total Visits Today
                                </div>
                                <div style={{ fontSize: '26px', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                                    {totalVisitsCount}
                                </div>
                            </div>
                        </Card.Body>
                    </StatCard>
                </Col>

                <Col md={3} sm={6} className="mb-3">
                    <StatCard>
                        <Card.Body style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px' }}>
                            <StatIcon bg="#ecfdf5" color="#059669">
                                <i className="fa fa-home"></i>
                            </StatIcon>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                    Household Visits
                                </div>
                                <div style={{ fontSize: '26px', fontWeight: 800, color: '#059669', lineHeight: 1.2 }}>
                                    {totalHouseholdVisits}
                                </div>
                            </div>
                        </Card.Body>
                    </StatCard>
                </Col>

                <Col md={3} sm={6} className="mb-3">
                    <StatCard>
                        <Card.Body style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px' }}>
                            <StatIcon bg="#fdf4ff" color="#9333ea">
                                <i className="fa fa-building"></i>
                            </StatIcon>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                    Community Apiaries
                                </div>
                                <div style={{ fontSize: '26px', fontWeight: 800, color: '#9333ea', lineHeight: 1.2 }}>
                                    {totalCommunityObservations}
                                </div>
                            </div>
                        </Card.Body>
                    </StatCard>
                </Col>

                <Col md={3} sm={6} className="mb-3">
                    <StatCard>
                        <Card.Body style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px' }}>
                            <StatIcon bg="#fff7ed" color="#ea580c">
                                <i className="fa fa-users"></i>
                            </StatIcon>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                    Active / Total Team
                                </div>
                                <div style={{ fontSize: '26px', fontWeight: 800, color: '#ea580c', lineHeight: 1.2 }}>
                                    {activeExecutives.length} <span style={{ fontSize: '16px', color: '#94a3b8' }}>/ {allExecutivesCount}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </StatCard>
                </Col>
            </Row>

            {/* WhatsApp Daily 9PM Digest Card */}
            <WhatsAppCard>
                <WhatsAppHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="fa fa-whatsapp" style={{ fontSize: '24px', color: '#4ade80' }}></i>
                        <div>
                            <span style={{ fontWeight: 800, fontSize: '15px' }}>
                                WhatsApp Daily 9 PM Digest Preview
                            </span>
                            <span style={{ marginLeft: '10px', fontSize: '12px', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '10px' }}>
                                {moment(selectedDate, 'YYYY-MM-DD').format('DD MMM YYYY')}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isEditingTitle ? (
                            <Form.Control
                                size="sm"
                                value={groupTitle}
                                onChange={(e) => setGroupTitle(e.target.value)}
                                onBlur={() => setIsEditingTitle(false)}
                                autoFocus
                                style={{ width: '180px', borderRadius: '8px' }}
                            />
                        ) : (
                            <Button 
                                variant="outline-light" 
                                size="sm" 
                                onClick={() => setIsEditingTitle(true)}
                                style={{ borderRadius: '8px', fontSize: '12px', padding: '4px 10px' }}
                                title="Edit Group Name"
                            >
                                <i className="fa fa-pencil mr-1"></i> {groupTitle}
                            </Button>
                        )}

                        <Button
                            variant={copied ? 'success' : 'light'}
                            size="sm"
                            onClick={handleCopy}
                            style={{ fontWeight: 700, borderRadius: '8px', padding: '6px 14px' }}
                        >
                            <i className={`fa ${copied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                            {copied ? 'Copied!' : 'Copy Text'}
                        </Button>

                        <Button
                            variant="success"
                            size="sm"
                            onClick={handleShareWhatsApp}
                            style={{ fontWeight: 700, borderRadius: '8px', padding: '6px 14px', backgroundColor: '#25d366', borderColor: '#25d366' }}
                        >
                            <i className="fa fa-whatsapp mr-1"></i> Share on WhatsApp
                        </Button>
                    </div>
                </WhatsAppHeader>

                <WhatsAppBody>
                    <MessageTextarea>
                        {whatsappMessage}
                    </MessageTextarea>
                </WhatsAppBody>
            </WhatsAppCard>

            {/* Navigation Tabs for Drill-down */}
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Nav variant="tabs" className="mb-3" style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="executives" 
                            style={{ fontWeight: 700, color: activeTab === 'executives' ? '#2563eb' : '#64748b' }}
                        >
                            <i className="fa fa-user-circle mr-2"></i> Executive Breakdown ({activeExecutives.length})
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="household" 
                            style={{ fontWeight: 700, color: activeTab === 'household' ? '#2563eb' : '#64748b' }}
                        >
                            <i className="fa fa-home mr-2"></i> Household Visits Log ({totalHouseholdVisits})
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="community" 
                            style={{ fontWeight: 700, color: activeTab === 'community' ? '#2563eb' : '#64748b' }}
                        >
                            <i className="fa fa-building mr-2"></i> Community Observations ({totalCommunityObservations})
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="inactive" 
                            style={{ fontWeight: 700, color: activeTab === 'inactive' ? '#dc2626' : '#64748b' }}
                        >
                            <i className="fa fa-clock-o mr-2"></i> No Visit Today ({inactiveExecutives.length})
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    {/* Tab 1: Executive Breakdown */}
                    <Tab.Pane eventKey="executives">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ color: '#475569', fontWeight: 600, fontSize: '14px' }}>
                                Field Executives Active on {moment(selectedDate, 'YYYY-MM-DD').format('DD MMMM YYYY')}
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Search executive by name or mobile..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ width: '280px', borderRadius: '10px' }}
                                size="sm"
                            />
                        </div>

                        {filteredActiveExecutives.length === 0 ? (
                            <div className="text-center p-5 bg-white rounded" style={{ border: '1px solid #e2e8f0' }}>
                                <i className="fa fa-calendar-times-o" style={{ fontSize: '36px', color: '#94a3b8' }}></i>
                                <h5 className="mt-3 text-muted">No visits recorded for this day</h5>
                                <p style={{ fontSize: '13px', color: '#94a3b8' }}>
                                    Try selecting a different date or refreshing the data.
                                </p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <CustomTable>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                                            <th>Executive Name</th>
                                            <th>Role</th>
                                            <th>Contact</th>
                                            <th style={{ textAlign: 'center' }}>Household Visits</th>
                                            <th style={{ textAlign: 'center' }}>Community Apiaries</th>
                                            <th style={{ textAlign: 'center' }}>Total Actions</th>
                                            <th style={{ textAlign: 'center' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredActiveExecutives.map((exec, idx) => {
                                            const total = exec.householdVisits.length + exec.communityObservations.length;
                                            return (
                                                <tr key={exec.id || idx}>
                                                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#94a3b8' }}>
                                                        {idx + 1}
                                                    </td>
                                                    <td>
                                                        {exec.id ? (
                                                            <a
                                                                href={`/user-profile/${exec.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ fontWeight: 700, color: '#2563eb', textDecoration: 'none' }}
                                                            >
                                                                {exec.name} 🔗
                                                            </a>
                                                        ) : (
                                                            <span style={{ fontWeight: 700, color: '#1e293b' }}>
                                                                {exec.name}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Badge variant="primary" style={{ backgroundColor: '#e0e7ff', color: '#4338ca', padding: '4px 8px', fontSize: '11px', fontWeight: 700 }}>
                                                            {exec.roles?.[0] || 'Bee Mitra'}
                                                        </Badge>
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                        {exec.mobile}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Badge pill style={{ backgroundColor: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', padding: '5px 12px', fontSize: '12px', fontWeight: 800 }}>
                                                            {exec.householdVisits.length} Visits
                                                        </Badge>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Badge pill style={{ backgroundColor: '#fdf4ff', color: '#9333ea', border: '1px solid #f0abfc', padding: '5px 12px', fontSize: '12px', fontWeight: 800 }}>
                                                            {exec.communityObservations.length} Apiaries
                                                        </Badge>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span style={{ fontWeight: 800, fontSize: '14px', color: '#1e293b' }}>
                                                            {total}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Badge variant="success" style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 700 }}>
                                                            🟢 Active Today
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </CustomTable>
                            </div>
                        )}
                    </Tab.Pane>

                    {/* Tab 2: Household Apiary Visits Log */}
                    <Tab.Pane eventKey="household">
                        {dayHouseholdLogs.length === 0 ? (
                            <div className="text-center p-5 bg-white rounded" style={{ border: '1px solid #e2e8f0' }}>
                                <i className="fa fa-home" style={{ fontSize: '36px', color: '#94a3b8' }}></i>
                                <h5 className="mt-3 text-muted">No household apiary visits on this date</h5>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <CustomTable>
                                    <thead>
                                        <tr>
                                            <th>Time</th>
                                            <th>Visited By</th>
                                            <th>Apiary / Beekeeper</th>
                                            <th>Colonies Status</th>
                                            <th>Health & Pests</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dayHouseholdLogs.map((log, idx) => {
                                            const visitor = log.visited_by_name || log.userName?.name || 'Visitor';
                                            const visitorId = log.visited_by_id?.$oid || log.visited_by_id || log.visited_by?._id;
                                            const time = moment(log.created_at || log.date).format('hh:mm A');
                                            const obs = log.observation || {};

                                            return (
                                                <tr key={log._id?.$oid || log._id || idx}>
                                                    <td style={{ fontWeight: 700, color: '#64748b' }}>
                                                        {time}
                                                    </td>
                                                    <td>
                                                        {visitorId ? (
                                                            <a 
                                                                href={`/user-profile/${visitorId}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                style={{ fontWeight: 700, color: '#2563eb', textDecoration: 'none' }}
                                                            >
                                                                {visitor} 🔗
                                                            </a>
                                                        ) : (
                                                            <span style={{ fontWeight: 700 }}>{visitor}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>
                                                        {log.apiaryName || log.apiary?.apiary_name || 'Household Apiary'}
                                                    </td>
                                                    <td>
                                                        <span style={{ color: '#059669', fontWeight: 700, marginRight: '8px' }}>
                                                            S: {obs.number_of_strong_colonies ?? 0}
                                                        </span>
                                                        <span style={{ color: '#d97706', fontWeight: 700, marginRight: '8px' }}>
                                                            W: {obs.number_of_weak_colonies ?? 0}
                                                        </span>
                                                        <span style={{ color: '#dc2626', fontWeight: 700, marginRight: '8px' }}>
                                                            Q-: {obs.number_of_colonies_without_queen ?? 0}
                                                        </span>
                                                        <span style={{ color: '#64748b', fontWeight: 700 }}>
                                                            E: {obs.number_of_empty_hives ?? 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Badge variant={obs.number_of_colonies_with_pests > 0 ? 'warning' : 'success'}>
                                                            {obs.number_of_colonies_with_pests > 0 ? 'Pests Detected' : 'Healthy'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <a
                                                            href="/household-apiaries"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ fontSize: '12px', fontWeight: 700, color: '#ff8701' }}
                                                        >
                                                            View in Apiaries ↗
                                                        </a>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </CustomTable>
                            </div>
                        )}
                    </Tab.Pane>

                    {/* Tab 3: Community Apiaries Observations Log */}
                    <Tab.Pane eventKey="community">
                        {dayObservations.length === 0 ? (
                            <div className="text-center p-5 bg-white rounded" style={{ border: '1px solid #e2e8f0' }}>
                                <i className="fa fa-building" style={{ fontSize: '36px', color: '#94a3b8' }}></i>
                                <h5 className="mt-3 text-muted">No community apiary observations on this date</h5>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <CustomTable>
                                    <thead>
                                        <tr>
                                            <th>Date & Time</th>
                                            <th>Apiary</th>
                                            <th>Observed By</th>
                                            <th style={{ textAlign: 'center' }}>Active Colonies</th>
                                            <th style={{ textAlign: 'center' }}>Empty Hives</th>
                                            <th style={{ textAlign: 'center' }}>Absconded</th>
                                            <th>Remarks</th>
                                            <th style={{ textAlign: 'center' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dayObservations.map((obs, idx) => {
                                            const time = moment(obs.observation_date || obs.created_at).format('DD MMM, hh:mm A');
                                            const apiaryName = apiaryNameMap[obs.apiary_id] || obs.apiary_id || 'Public Apiary';
                                            const observerId = obs.observed_by?.$oid || obs.observed_by?._id || obs.observed_by;
                                            const userObj = observerId ? usersMap.idMap[observerId.toString()] : null;
                                            const observerName = userObj?.name || obs.observed_by_name || 'Observer';

                                            return (
                                                <tr key={obs._id?.$oid || obs._id || idx}>
                                                    <td style={{ fontWeight: 700, color: '#64748b' }}>
                                                        {time}
                                                    </td>
                                                    <td>
                                                        <a
                                                            href={`/nector-apiaries/${obs.apiary_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ fontWeight: 700, color: '#2563eb', textDecoration: 'none' }}
                                                        >
                                                            {apiaryName} ↗
                                                        </a>
                                                    </td>
                                                    <td>
                                                        {observerId ? (
                                                            <a
                                                                href={`/user-profile/${observerId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ fontWeight: 700, color: '#059669', textDecoration: 'none' }}
                                                            >
                                                                {observerName} 🔗
                                                            </a>
                                                        ) : (
                                                            <span style={{ fontWeight: 700 }}>{observerName}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center', color: '#059669', fontWeight: 800 }}>
                                                        {obs.active_colonies ?? 0}
                                                    </td>
                                                    <td style={{ textAlign: 'center', color: '#dc2626', fontWeight: 800 }}>
                                                        {obs.empty_hives ?? 0}
                                                    </td>
                                                    <td style={{ textAlign: 'center', color: '#64748b', fontWeight: 800 }}>
                                                        {obs.absconded_hives ?? 0}
                                                    </td>
                                                    <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {obs.remarks || '-'}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => setSelectedObservation(obs)}
                                                            style={{ color: '#ff8701', fontWeight: 700, padding: 0 }}
                                                        >
                                                            View Details
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </CustomTable>
                            </div>
                        )}
                    </Tab.Pane>

                    {/* Tab 4: Inactive Team Members */}
                    <Tab.Pane eventKey="inactive">
                        <div style={{ padding: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <h5 style={{ fontWeight: 700, color: '#dc2626', marginBottom: '8px' }}>
                                ⏸️ Team Members with No Recorded Visits Today ({inactiveExecutives.length})
                            </h5>
                            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
                                These registered BeeMitras and field staff have not logged any household visits or community apiary observations on {moment(selectedDate, 'YYYY-MM-DD').format('DD MMMM YYYY')}.
                            </p>

                            <Row>
                                {inactiveExecutives.map((exec, idx) => (
                                    <Col md={4} sm={6} key={exec.id || idx} className="mb-2">
                                        <div style={{ 
                                            background: '#f8fafc', 
                                            border: '1px solid #e2e8f0', 
                                            borderRadius: '8px', 
                                            padding: '10px 14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#334155', fontSize: '13.5px' }}>
                                                    {exec.name}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace' }}>
                                                    {exec.mobile}
                                                </div>
                                            </div>
                                            <Badge variant="secondary" style={{ fontSize: '10px' }}>
                                                0 Visits
                                            </Badge>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Observation Details Modal */}
            {selectedObservation && (
                <Modal show={Boolean(selectedObservation)} onHide={() => setSelectedObservation(null)} centered size="lg">
                    <Modal.Header closeButton style={{ background: '#f8fafc' }}>
                        <Modal.Title style={{ fontWeight: 800, color: '#1e293b', fontSize: '18px' }}>
                            Apiary Observation Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '24px' }}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>APIARY</div>
                                <div style={{ fontWeight: 800, fontSize: '16px', color: '#2563eb' }}>
                                    {apiaryNameMap[selectedObservation.apiary_id] || selectedObservation.apiary_id}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>DATE OF OBSERVATION</div>
                                <div style={{ fontWeight: 700, fontSize: '15px' }}>
                                    {moment(selectedObservation.observation_date || selectedObservation.created_at).format('DD MMM YYYY, hh:mm A')}
                                </div>
                            </Col>
                        </Row>

                        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                            <Row>
                                <Col xs={3} className="text-center">
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>ACTIVE</div>
                                    <div style={{ fontSize: '24px', fontWeight: 800, color: '#059669' }}>
                                        {selectedObservation.active_colonies ?? 0}
                                    </div>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>EMPTY</div>
                                    <div style={{ fontSize: '24px', fontWeight: 800, color: '#dc2626' }}>
                                        {selectedObservation.empty_hives ?? 0}
                                    </div>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>ABSCONDED</div>
                                    <div style={{ fontSize: '24px', fontWeight: 800, color: '#64748b' }}>
                                        {selectedObservation.absconded_hives ?? 0}
                                    </div>
                                </Col>
                                <Col xs={3} className="text-center">
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>TOTAL HIVES</div>
                                    <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>
                                        {selectedObservation.total_hives ?? 0}
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {selectedObservation.remarks && (
                            <div className="mb-3">
                                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700, marginBottom: '4px' }}>REMARKS</div>
                                <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', fontSize: '13px' }}>
                                    {selectedObservation.remarks}
                                </div>
                            </div>
                        )}

                        {selectedObservation.apiary_photos && selectedObservation.apiary_photos.length > 0 && (
                            <div>
                                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700, marginBottom: '8px' }}>PHOTOS</div>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    {selectedObservation.apiary_photos.map((photo, i) => (
                                        <img
                                            key={i}
                                            src={photo.url || photo}
                                            alt={`Apiary observation ${i + 1}`}
                                            style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setSelectedObservation(null)}>
                            Close
                        </Button>
                        <Button 
                            variant="primary" 
                            href={`/nector-apiaries/${selectedObservation.apiary_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open Full Apiary Page ↗
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </MainWrapper>
    );
}
